import { flow, getEnv, types } from 'mobx-state-tree';

import { ItemLoadingStateEnum } from './LoadingStateEnums';
import { AdvancedStoreEnv } from './StoreEnv';
import UserFireConfigModel, {
  createUserFireConfigModel
} from './UserFireConfigModel';

const UserFireConfigStore = types
  .model('UserFireConfigStore', {
    itemLoadingState: types.maybe(ItemLoadingStateEnum),
    userFireConfig: types.maybe(UserFireConfigModel)
  })
  .actions((self) => {
    const getUserFireConfig = flow(function* (organizationId: number) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.itemLoadingState = 'loading';
        const result: any = yield client.getUserFireConfig(organizationId);

        self.userFireConfig = createUserFireConfigModel(result);
        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'UserFireConfigStore | getUserFireConfig',
            error,
            error.body
          );
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const updateUserFireConfig = flow(function* (
      organizationId: number,
      patch: any
    ) {
      const { client, applicationStore } = getEnv<AdvancedStoreEnv>(self);

      try {
        self.itemLoadingState = 'saving';
        const result: any = yield client.updateUserFireConfig(
          organizationId,
          patch
        );

        self.userFireConfig = createUserFireConfigModel(result);
        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'UserFireConfigStore | updateUserFireConfig',
            error,
            error.body
          );
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'save_error';
        return undefined;
      }
    });

    const getRegions = flow(function* () {
      const { client }: AdvancedStoreEnv = getEnv(self);

      return yield client.getRegions();
    });

    const getIndustries = flow(function* () {
      const { client }: AdvancedStoreEnv = getEnv(self);

      return yield client.getIndustries();
    });

    const getFlavors = flow(function* () {
      const { client }: AdvancedStoreEnv = getEnv(self);

      return yield client.getFlavors();
    });

    return {
      getUserFireConfig,
      updateUserFireConfig,
      getRegions,
      getIndustries,
      getFlavors
    };
  })
  .views((self) => {
    return {
      get isItemLoading(): boolean {
        return (
          self.itemLoadingState === 'loading' ||
          self.userFireConfig === undefined
        );
      },
      get isItemLoadError(): boolean {
        return self.itemLoadingState === 'load_error';
      }
    };
  });

export type UserFireConfigStoreType = typeof UserFireConfigStore.Type;
export default UserFireConfigStore;
